import {
  AccountsRepository,
  RegisterAccountInput,
} from '@lib/api/repository/accounts/accounts.repository';
import { Account, AdminAccount } from '@lib/types';
import { AdminPage, AdminPageFilters } from '@lib/types/admin-page';

import { Requester } from '../clients';

import {
  AccountDTO,
  AdminAccountDTO,
  createAccount,
  createAdminAccount,
} from './models';

class AccountsAPI implements AccountsRepository {
  async listAdminPage(
    filters: AdminPageFilters,
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminAccount>> {
    const result = (
      await Requester.get<AdminPage<AdminAccountDTO>>('/accounts/page', {
        params: { ...filters },
        signal,
      })
    ).data;

    return { items: result.items.map(createAdminAccount), total: result.total };
  }

  async setCPF(cpf: string): Promise<Account> {
    const response = await Requester.patch<AccountDTO>('/accounts/cpf', {
      body: { cpf },
    });

    return createAccount(response.data);
  }

  async register(data: RegisterAccountInput): Promise<Account> {
    const response = await Requester.post<AccountDTO>('/accounts/register', {
      body: data,
    });

    return createAccount(response.data);
  }

  async list(signal?: AbortSignal): Promise<AdminAccount[]> {
    const response = await Requester.get<AdminAccountDTO[]>('/accounts', {
      signal,
    });

    return response.data.map(createAdminAccount);
  }

  async create(data: Account): Promise<Account> {
    const response = await Requester.post<AccountDTO>('/accounts', {
      body: {
        cpf: data.cpf,
        name: data.name,
        email: data.email,
        birthDate: data.birthDate,
        phone: data.phone,
      },
    });

    return createAccount(response.data);
  }

  async update(data: Account): Promise<Account> {
    const response = await Requester.put<AccountDTO>('/accounts', {
      body: {
        name: data.name,
        birthDate: data.birthDate,
        sex: data.sex,
        phone: data.phone,
      },
    });

    return createAccount(response.data);
  }

  async updateProfilePhoto(photo: File): Promise<Account> {
    const formData = new FormData();

    formData.append('photo', photo);

    const response = await Requester.put<AccountDTO>(
      '/accounts/profile-photo',
      {
        body: formData,
      },
    );

    return createAccount(response.data);
  }

  async updatePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    await Requester.patch('/accounts/password', {
      body: {
        oldPassword,
        newPassword,
      },
    });
  }

  async askVerificationCode(): Promise<void> {
    await Requester.post<void>('/accounts/verification-code');
  }

  async verify(code: string): Promise<Account> {
    const response = await Requester.patch<AccountDTO>('/accounts/verify', {
      body: { code },
    });

    return createAccount(response.data);
  }

  async exportData(signal?: AbortSignal) {
    const response = await Requester.get<
      Awaited<ReturnType<AccountsRepository['exportData']>>
    >('/accounts/export', { signal });

    return response.data;
  }

  async deleteAccount(password: string): Promise<void> {
    await Requester.delete('/accounts', { body: { password } });
  }

  async setActive(id: string, active: boolean): Promise<AdminAccount> {
    const response = (
      await Requester.patch<AdminAccountDTO>(
        `/accounts/admin/${id}/active/${active}`,
      )
    ).data;

    return createAdminAccount(response);
  }
}

export default AccountsAPI;
